import React, { useState, useEffect } from 'react';
import { 
  Globe2, 
  Building2, 
  PiggyBank, 
  Users, 
  ChevronRight, 
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  BarChart3,
  Target,
  Award,
  MapPin
} from 'lucide-react';

import image from '../../Assets/Images/trial/13-.jpg'
import {Helmet} from "react-helmet-async";

// Componente de estadísticas animadas
const StatCard = ({ icon: Icon, value, label }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const duration = 2000;
    const steps = 50;
    const increment = value / steps;
    const interval = duration / steps;
    
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current > value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, interval);
    
    return () => clearInterval(timer);
  }, [value]);
  
  return (
    <div className="bg-white rounded-lg p-6 shadow-lg transform hover:-translate-y-1 transition-all duration-300">
      <Icon className="w-8 h-8 text-[#74ACDF] mb-4" />
      <div className="text-3xl font-bold text-gray-800 mb-2">
        {count}+
      </div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

// Componente de testimonio
const TestimonialCard = ({ name, role, content, image }) => (
  <div className="bg-white p-8 rounded-lg shadow-lg mx-4 my-2 relative">
    <div className="absolute top-0 right-0 w-20 h-20 bg-[#74ACDF] opacity-10 rounded-bl-full" />
    <div className="mb-6">
      <img src={image} alt={name} className="w-16 h-16 rounded-full border-4 border-[#74ACDF]" />
    </div>
    <p className="text-gray-600 mb-4 italic">"{content}"</p>
    <div>
      <p className="font-bold text-gray-800">{name}</p>
      <p className="text-[#74ACDF]">{role}</p>
    </div>
  </div>
);

// Componente principal de servicios mejorado
const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  const services = [
    {
      id: 1,
      title: "Business Setup Services",
      icon: Globe2,
      description: "Expert guidance for companies looking to expand globally. We help navigate international markets, regulations, and cultural differences to ensure successful expansion.",
      features: [
        "Market Entry Strategy",
        "International Compliance",
        "Cross-border Operations",
        "Cultural Integration"
      ],
      stats: {
        success: "98%",
        countries: "50+",
        clients: "200+"
      }
    },
    {
      id: 2,
      title: "Real Estate Consulting",
      icon: Building2,
      description: "Strategic real estate investment advice for both commercial and residential properties. We analyze markets, identify opportunities, and optimize your property portfolio.",
      features: [
        "Market Analysis",
        "Investment Strategy",
        "Portfolio Optimization",
        "Property Management"
      ],
      stats: {
        properties: "1000+",
        roi: "25%",
        satisfaction: "96%"
      }
    },
    {
      id: 3,
      title: "Accounting Consulting",
      icon: PiggyBank,
      description: "Comprehensive investment solutions for individuals and businesses. Our experts develop tailored strategies to maximize returns while managing risk effectively.",
      features: [
        "Portfolio Management",
        "Risk Assessment",
        "Wealth Planning",
        "Asset Allocation"
      ],
      stats: {
        aum: "$500M+",
        returns: "18%",
        clients: "300+"
      }
    }
  ];

  const testimonials = [
    {
      name: "John Smith",
      role: "CEO, Global Ventures",
      content: "Their business globalization services transformed our company's international presence. The expertise and guidance provided were invaluable.",
      image: "/api/placeholder/64/64"
    },
    {
      name: "Sarah Johnson",
      role: "Real Estate Investor",
      content: "The real estate consulting team helped me build a profitable portfolio. Their market insights and strategic approach exceeded my expectations.",
      image: "/api/placeholder/64/64"
    },
    {
      name: "Michael Chen",
      role: "Investment Director",
      content: "Outstanding investment services with a personal touch. They truly understand how to balance risk and returns in today's market.",
      image: "/api/placeholder/64/64"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
      <section className="overflow-hidden">
        <Helmet>
          <meta name="keywords" content="Setup Business in Dubai, Business Setup, Business Solutions in Dubai "/>
          <title>Business Solutions Dubai - Our Services</title>
          <meta name="description"
                content="Business Solutions Dubai offers tailored business zones designed to accelerate your growth. From tech innovation to real estate, Dubai offers unparalleled opportunities for global entrepreneurs."/>
          <meta name="robots" content="index, follow"/>
          <meta name="lenguage" content="english"/>
          <meta name="publisher" content="Business Solutions Dubai"/>
          <link rel="canonical" href="https://www.businesssolutionsdubai.com"/>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Business Solutions Dubai",
              "url": "https://www.tu-sitio.com",
              "logo": "https://www.tu-sitio.com/logo.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+971-123-456-789",
                "contactType": "Customer Service",
                "areaServed": "AE",
                "availableLanguage": ["en", "ar"]
              },
              "sameAs": [
                "https://www.facebook.com/tuempresa",
                "https://www.twitter.com/tuempresa",
                "https://www.linkedin.com/company/tuempresa"
              ],
              "description": "Especialistas en globalización de empresas en Dubái. Ofrecemos servicios de asesoría para establecer compañías en zonas francas, mainland y offshore, con experiencia en todos los procesos legales y financieros.",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Business Bay, Burj Khalifa Street",
                "addressLocality": "Dubai",
                "postalCode": "00000",
                "addressCountry": "AE"
              },
              "areaServed": "AE",
              "offers": {
                "@type": "Offer",
                "url": "https://www.tu-sitio.com/servicios",
                "priceCurrency": "USD",
                "category": [
                  "Zonas Francas",
                  "Mainland",
                  "Offshore"
                ],
                "itemOffered": {
                  "@type": "Service",
                  "name": "Asesoría para establecer empresas en Dubái",
                  "description": "Servicios de consultoría para la globalización de negocios, incluyendo registro de empresas en zonas francas y mainland."
                }
              }
            })}
          </script>
        </Helmet>
        {/* Header con forma personalizada */}
        <div className="relative bg-gradient-to-r from-[#74ACDF] to-blue-600 py-60">
          <div className="absolute bottom-0 left-0 w-full h-16 bg-white transform -skew-y-3"/>
          <div className="max-w-7xl mx-auto px-4 text-center relative">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Services
            </h2>
            <p className="text-white/90 max-w-2xl mx-auto text-lg">
              Comprehensive solutions for businesses and individuals looking to expand globally,
              invest wisely, and grow their wealth through strategic planning and expert guidance.
            </p>
          </div>
        </div>

        {/* Navegación de servicios */}
        <div className="max-w-7xl mx-auto px-4 mt-16">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {services.map((service, idx) => (
                <button
                    key={service.id}
                    onClick={() => setActiveTab(idx)}
                    className={`flex items-center px-6 py-3 rounded-full transition-all ${
                        activeTab === idx
                            ? 'bg-[#74ACDF] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <service.icon className="w-5 h-5 mr-2"/>
                  {service.title}
                </button>
            ))}
          </div>

          {/* Contenido del servicio activo */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-[#74ACDF]/10 text-[#74ACDF] rounded-full">
                <Award className="w-5 h-5 mr-2"/>
                Premium Service
              </div>
              <h3 className="text-3xl font-bold text-gray-800">
                {services[activeTab].title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {services[activeTab].description}
              </p>
              <ul className="space-y-3">
                {services[activeTab].features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-[#74ACDF] mr-3"/>
                      {feature}
                    </li>
                ))}
              </ul>
              <a
                  href="https://wa.link/txunpz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[220px] flex items-center px-8 py-4 bg-[#74ACDF] text-white rounded-lg hover:bg-blue-600 transition-colors group">
                Learn More
                <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform"/>
              </a>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <StatCard icon={Users} value={500} label="Happy Clients"/>
              <StatCard icon={Globe2} value={50} label="Countries Served"/>
              <StatCard icon={TrendingUp} value={95} label="Success Rate"/>
              <StatCard icon={BarChart3} value={25} label="Avg. ROI %"/>
            </div>
          </div>



          {/* Sección de estadísticas globales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 my-20">
            <div className="text-center">
              <Target className="w-8 h-8 text-[#74ACDF] mx-auto mb-4"/>
              <h4 className="text-2xl font-bold text-gray-800">15+</h4>
              <p className="text-gray-600">Years Experience</p>
            </div>
            <div className="text-center">
              <Users className="w-8 h-8 text-[#74ACDF] mx-auto mb-4"/>
              <h4 className="text-2xl font-bold text-gray-800">1000+</h4>
              <p className="text-gray-600">Clients Worldwide</p>
            </div>
            <div className="text-center">
              <MapPin className="w-8 h-8 text-[#74ACDF] mx-auto mb-4"/>
              <h4 className="text-2xl font-bold text-gray-800">50+</h4>
              <p className="text-gray-600">Countries Served</p>
            </div>
            <div className="text-center">
              <Award className="w-8 h-8 text-[#74ACDF] mx-auto mb-4"/>
              <h4 className="text-2xl font-bold text-gray-800">98%</h4>
              <p className="text-gray-600">Success Rate</p>
            </div>
          </div>
        </div>
      </section>
  );
};

export default ServicesSection;